import { EMOJI_SETS } from "@/constants/emojiSets";
import type { TutorialStep } from "@/constants/setPowers";
import { SET_POWERS } from "@/constants/setPowers";
import { getAllowedMovesMap, isValidMove } from "@/hooks/useChessgroundConfig";
import type { Square } from "chess.js";
import { Chess } from "chess.js";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface TutorialState {
  // Tutorial progression
  currentStepIndex: number;
  isActive: boolean;
  isProcessingClick: boolean;

  // Game state for tutorial
  game: Chess;
  whiteTime: number;
  blackTime: number;
  powerState: {
    counters: Record<string, unknown>;
    usage: Record<string, { total: number; thisTurn: number }>;
  };

  // Custom piece tracking (matching GameState)
  customPieces: Map<
    Square,
    {
      type: "seed" | "custom";
      color: "w" | "b";
      powerId: string;
      metadata?: Record<string, unknown>;
      createdTurn: number;
    }
  >;

  // Additional GameState-like properties for rules engine compatibility
  selectedPiece: Square | null;
  isPlayerTurn: boolean;
  playerColor: "w" | "b";
  clockIsRunning: boolean;
  playerSet: (typeof EMOJI_SETS)[0] | null;
  opponentSet: (typeof EMOJI_SETS)[0] | null;
  unlockedSets: Set<string>;
  selectionPhase: "player" | "opponent";
  aiSkillLevel: number;
  allowedMoves: ((square: Square) => string[]) | null;

  // Change tracking for validation
  changeTracker: {
    timeChanged: boolean;
    counterChanged: boolean;
    triggeredPowerId?: string;
    previousTime: number;
    previousCounters: Record<string, unknown>;
  };

  // UI state
  showTooltip: boolean;
  tooltipPosition: { x: number; y: number };
  errorMessage: string | null;

  // Tutorial configuration
  tutorialSteps: TutorialStep[];
  setId: string;

  // Actions
  initializeTutorial: (
    steps: TutorialStep[],
    setId: string,
    opponentSetId: string
  ) => void;
  startTutorial: () => void;
  resetTutorial: () => void;
  updateAllowedMovesFromStep: () => void;

  // Step management
  advanceStep: () => void;
  setCurrentStep: (index: number) => void;

  // GameState-like methods for rules engine compatibility
  getCounter: (key: string) => unknown;
  setCounter: (key: string, value: unknown) => void;
  setPowerState: (
    partial: Partial<{
      counters: Record<string, unknown>;
      usage: Record<string, { total: number; thisTurn: number }>;
    }>
  ) => void;
  setPowerCounter: (key: string, value: unknown) => void;
  resetAllowedMoves: () => void;
  setAllowedMoves: (moves: ((square: Square) => string[]) | null) => void;
  setSelectedPiece: (piece: Square | null) => void;

  // Custom piece methods (matching GameState)
  addCustomPiece: (
    square: Square,
    type: "seed" | "custom",
    color: "w" | "b",
    powerId: string,
    metadata?: Record<string, unknown>
  ) => void;
  removeCustomPiece: (square: Square) => void;
  getCustomPiece: (square: Square) => {
    type: "seed" | "custom";
    color: "w" | "b";
    powerId: string;
    metadata?: Record<string, unknown>;
    createdTurn: number;
  } | null;
  isCustomPiece: (square: Square) => boolean;
  getCustomPiecesByPower: (powerId: string) => Array<{
    square: Square;
    type: "seed" | "custom";
    color: "w" | "b";
    powerId: string;
    metadata?: Record<string, unknown>;
    createdTurn: number;
  }>;
  clearCustomPieces: () => void;

  // Time management with change tracking
  setWhiteTime: (time: number) => void;
  setBlackTime: (time: number) => void;

  // Change tracking
  resetChangeTracker: () => void;

  // Validation
  validateCurrentStep: (triggeredPowerId?: string) => boolean;
  validateMove: (from: Square, to: Square) => boolean;
  getValidMovesMap: () => Map<string, string[]>;

  // UI actions
  setTooltip: (show: boolean, position?: { x: number; y: number }) => void;
  setErrorMessage: (message: string | null) => void;
  setProcessingClick: (processing: boolean) => void;

  // Internal method for advancing without validation (used by auto-advancement)
  internalAdvanceStep: () => void;
}

export const useTutorialStore = create<TutorialState>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentStepIndex: 0,
      isActive: false,
      isProcessingClick: false,

      game: new Chess(),
      whiteTime: 300000,
      blackTime: 300000,
      powerState: {
        counters: {},
        usage: {},
      },

      // Custom piece tracking (matching GameState)
      customPieces: new Map(),

      // Additional GameState-like properties
      selectedPiece: null,
      isPlayerTurn: true,
      playerColor: "w",
      clockIsRunning: true,
      playerSet: null,
      opponentSet: null,
      unlockedSets: new Set(),
      selectionPhase: "player",
      aiSkillLevel: 10,
      allowedMoves: null,

      changeTracker: {
        timeChanged: false,
        counterChanged: false,
        previousTime: 300000,
        previousCounters: {},
      },

      showTooltip: false,
      tooltipPosition: { x: 0, y: 0 },
      errorMessage: null,

      tutorialSteps: [],
      setId: "",

      // Actions
      initializeTutorial: (steps, setId, opponentSetId) => {
        set({
          tutorialSteps: steps,
          setId,
          currentStepIndex: 0,
          isActive: false,
          game: new Chess(),
          whiteTime: 300000,
          blackTime: 300000,
          powerState: {
            counters: {},
            usage: {},
          },
          selectedPiece: null,
          isPlayerTurn: true,
          playerColor: "w",
          clockIsRunning: true,
          playerSet: EMOJI_SETS.find((set) => set.id === setId) || null, // Set proper player set for rules engine
          opponentSet:
            EMOJI_SETS.find((set) => set.id === opponentSetId) || null,
          allowedMoves: null,
          changeTracker: {
            timeChanged: false,
            counterChanged: false,
            previousTime: 300000,
            previousCounters: {},
          },
          errorMessage: null,
        });
        // Update allowed moves for the initial step
        get().updateAllowedMovesFromStep();
      },

      startTutorial: () => {
        const { tutorialSteps } = get();
        const firstStep = tutorialSteps[0];

        // Check if the first step has a custom FEN
        let newGame = new Chess();
        if (firstStep?.fen) {
          try {
            newGame = new Chess(firstStep.fen);
            console.log(`Loading custom FEN for first step:`, firstStep.fen);
          } catch (error) {
            console.error(`Invalid FEN for first step:`, firstStep.fen, error);
            // Keep the default starting position if FEN is invalid
          }
        }

        set({
          isActive: true,
          currentStepIndex: 0,
          game: newGame,
        });
        // Update allowed moves for the first step
        get().updateAllowedMovesFromStep();
      },

      resetTutorial: () => {
        set({
          currentStepIndex: 0,
          isActive: false,
          game: new Chess(),
          whiteTime: 300000,
          blackTime: 300000,
          powerState: {
            counters: {},
            usage: {},
          },
          selectedPiece: null,
          isPlayerTurn: true,
          playerColor: "w",
          clockIsRunning: true,
          allowedMoves: null,
          changeTracker: {
            timeChanged: false,
            counterChanged: false,
            previousTime: 300000,
            previousCounters: {},
          },
          errorMessage: null,
          showTooltip: false,
        });
      },

      advanceStep: () => {
        const { currentStepIndex, tutorialSteps, powerState, setId } = get();
        const currentStep = tutorialSteps[currentStepIndex];
        const setPowerInfo = SET_POWERS[setId];

        // Check if we need to validate counter thresholds before advancing
        if (setPowerInfo?.tutorialConfig?.counterAcceleration && currentStep) {
          const { counterKey, maxValue, autoAdvanceStepId } =
            setPowerInfo.tutorialConfig.counterAcceleration;

          // If this step requires reaching a counter threshold, check it
          if (autoAdvanceStepId === currentStep.id) {
            const counter = powerState.counters[counterKey];

            if (counter && typeof counter === "object" && "count" in counter) {
              const counterObj = counter as { count: number };

              // Only advance if we have enough counter value
              if (counterObj.count < maxValue) {
                return; // Don't advance yet, but don't show error
              }
            }
          }
        }

        // Use internal advancement method
        get().internalAdvanceStep();
      },

      // Internal method for advancing without validation (used by auto-advancement)
      internalAdvanceStep: () => {
        const { currentStepIndex, tutorialSteps } = get();
        if (currentStepIndex < tutorialSteps.length - 1) {
          const nextStepIndex = currentStepIndex + 1;
          const nextStep = tutorialSteps[nextStepIndex];

          // Check if the next step has a custom FEN
          let newGame = get().game;
          if (nextStep.fen) {
            try {
              newGame = new Chess(nextStep.fen);
              console.log(
                `Loading custom FEN for step ${nextStepIndex}:`,
                nextStep.fen
              );
            } catch (error) {
              console.error(
                `Invalid FEN for step ${nextStepIndex}:`,
                nextStep.fen,
                error
              );
              // Keep the current game state if FEN is invalid
            }
          }

          set({
            currentStepIndex: nextStepIndex,
            game: newGame,
            errorMessage: null,
          });
          // Update allowed moves for the new step
          get().updateAllowedMovesFromStep();
        } else {
          set({
            isActive: false,
            errorMessage: null,
          });
        }
      },

      setCurrentStep: (index) => {
        const { tutorialSteps } = get();
        const targetStep = tutorialSteps[index];

        // Check if the target step has a custom FEN
        let newGame = get().game;
        if (targetStep?.fen) {
          try {
            newGame = new Chess(targetStep.fen);
            console.log(
              `Loading custom FEN for step ${index}:`,
              targetStep.fen
            );
          } catch (error) {
            console.error(
              `Invalid FEN for step ${index}:`,
              targetStep.fen,
              error
            );
            // Keep the current game state if FEN is invalid
          }
        }

        set({
          currentStepIndex: index,
          game: newGame,
        });
        // Update allowed moves for the new step
        get().updateAllowedMovesFromStep();
      },

      // GameState-like methods for rules engine compatibility
      getCounter: (key: string) => {
        const { powerState } = get();
        return powerState.counters[key] ?? 0;
      },

      setCounter: (key: string, value: unknown) => {
        const { powerState, changeTracker } = get();

        set({
          powerState: {
            ...powerState,
            counters: { ...powerState.counters, [key]: value },
          },
          changeTracker: {
            ...changeTracker,
            counterChanged: true, // Mark that a counter changed
          },
        });
      },

      setPowerState: (
        partial: Partial<{
          counters: Record<string, unknown>;
          usage: Record<string, { total: number; thisTurn: number }>;
        }>
      ) => {
        const { powerState } = get();
        set({
          powerState: {
            ...powerState,
            ...partial,
          },
        });
      },

      setPowerCounter: (key: string, value: unknown) => {
        const { powerState, changeTracker } = get();
        set({
          powerState: {
            ...powerState,
            counters: { ...powerState.counters, [key]: value },
          },
          changeTracker: {
            ...changeTracker,
            counterChanged: true, // Mark that a counter changed
          },
        });
      },

      resetAllowedMoves: () => {
        set({ allowedMoves: null });
      },

      setAllowedMoves: (moves) => {
        set({ allowedMoves: moves });
      },

      setSelectedPiece: (piece: Square | null) => {
        set({ selectedPiece: piece });
      },

      resetChangeTracker: () => {
        const { whiteTime, powerState } = get();
        set({
          changeTracker: {
            timeChanged: false,
            counterChanged: false,
            previousTime: whiteTime,
            previousCounters: { ...powerState.counters },
          },
        });
      },

      validateCurrentStep: (triggeredPowerId) => {
        const { tutorialSteps, currentStepIndex, changeTracker, game, setId } =
          get();
        const currentStep = tutorialSteps[currentStepIndex];
        const setPowerInfo = SET_POWERS[setId];

        if (!currentStep.validation) {
          return true;
        }

        const validation = currentStep.validation;

        if (
          validation.powerTriggered &&
          validation.powerTriggered !== triggeredPowerId
        ) {
          return false;
        }

        if (validation.counterChanged && !changeTracker.counterChanged) {
          return false;
        }

        if (validation.timeDecreased && !changeTracker.timeChanged) {
          return false;
        }

        if (validation.pieceAdded) {
          const piece = game.get(currentStep.targetSquare as Square);
          if (!piece) {
            return false;
          }
        }

        // Generic handling for counter accumulation steps
        if (setPowerInfo?.tutorialConfig?.counterAcceleration) {
          const { autoAdvanceStepId } =
            setPowerInfo.tutorialConfig.counterAcceleration;

          if (autoAdvanceStepId === currentStep.id) {
            // For counter accumulation steps, we want to validate the action is correct
            // but only advance when we have enough counter value

            // First check if the basic validation passed (correct power triggered, etc.)
            if (
              validation.powerTriggered &&
              validation.powerTriggered !== triggeredPowerId
            ) {
              return false; // Wrong power triggered
            }

            if (validation.counterChanged && !changeTracker.counterChanged) {
              return false; // Counter didn't change
            }

            // If we get here, the action was correct, so return true
            // The advanceStep method will handle whether to actually advance
            return true;
          }
        }

        return true;
      },

      validateMove: (from: Square, to: Square) => {
        const { game, allowedMoves } = get();
        return isValidMove(from, to, game, allowedMoves);
      },

      setTooltip: (show, position) => {
        set({
          showTooltip: show,
          tooltipPosition: position || { x: 0, y: 0 },
        });
      },

      setErrorMessage: (message) => {
        set({ errorMessage: message });
      },

      setProcessingClick: (processing) => {
        set({ isProcessingClick: processing });
      },

      getValidMovesMap: () => {
        const { game, allowedMoves } = get();
        return getAllowedMovesMap(game, allowedMoves);
      },

      updateAllowedMovesFromStep: () => {
        const { tutorialSteps, currentStepIndex } = get();
        const currentStep = tutorialSteps[currentStepIndex];

        if (currentStep?.validMoves) {
          // Convert Record<string, string[]> to allowedMoves function
          const validMovesMap = currentStep.validMoves;
          const allowedMovesFunction = (square: Square): string[] => {
            const moves = validMovesMap[square] || [];
            return moves;
          };

          set({ allowedMoves: allowedMovesFunction });
        } else {
          // No valid moves specified, reset to null (use normal chess rules)
          set({ allowedMoves: null });
        }
      },

      // Time management with change tracking
      setWhiteTime: (time: number) => {
        const { changeTracker } = get();
        set({
          whiteTime: time,
          changeTracker: {
            ...changeTracker,
            timeChanged: true, // Mark that time changed
          },
        });
      },

      setBlackTime: (time: number) => {
        const { changeTracker } = get();
        set({
          blackTime: time,
          changeTracker: {
            ...changeTracker,
            timeChanged: true, // Mark that time changed
          },
        });
      },

      // Custom piece methods (matching GameState)
      addCustomPiece: (
        square: Square,
        type: "seed" | "custom",
        color: "w" | "b",
        powerId: string,
        metadata?: Record<string, unknown>
      ) => {
        set({
          customPieces: new Map(get().customPieces).set(square, {
            type,
            color,
            powerId,
            metadata,
            createdTurn: get().currentStepIndex,
          }),
        });
      },

      removeCustomPiece: (square: Square) => {
        const { customPieces } = get();
        customPieces.delete(square);
        set({ customPieces: new Map(customPieces) });
      },

      getCustomPiece: (square: Square) => {
        const { customPieces } = get();
        return customPieces.get(square) || null;
      },

      isCustomPiece: (square: Square) => {
        const { customPieces } = get();
        return customPieces.has(square);
      },

      getCustomPiecesByPower: (powerId: string) => {
        const { customPieces } = get();
        return Array.from(customPieces.entries())
          .filter(([, piece]) => piece.powerId === powerId)
          .map(([square, piece]) => ({
            square,
            ...piece,
          }));
      },

      clearCustomPieces: () => {
        set({ customPieces: new Map() });
      },
    }),
    {
      name: "tutorial-store",
    }
  )
);

